
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface ArtworkItem {
  id: string;
  title: string;
  medium: string;
  year: string;
  dimensions: string;
  image: string;
  description: string;
  category: string;
}

const ArtworkGallery = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArtworkIndex, setSelectedArtworkIndex] = useState<number | null>(null);

  const artworks: ArtworkItem[] = [
    {
      id: 'buffed-out',
      title: 'Buffed Out',
      medium: 'Documentary Photography',
      year: '2012',
      dimensions: '16" x 24"',
      image: 'https://static.readdy.ai/image/3fdc746f0d1d84f2cde4df046cd392e8/5ca64014c51f69806ed7af8c169b4de4.jpeg',
      description: 'Buffed Out explores the unintended aesthetics created when graffiti is painted over or "buffed out." These visual remnants — uneven patches, mismatched tones, and layered textures — form compositions that recall modernist and Abstract Expressionist painting. Through documentary photography, the series invites viewers to question whether these accidental abstractions are more visually compelling than the graffiti they conceal. By highlighting the tension between creation and erasure, Buffed Out examines how acts of removal can generate new meaning, transforming public walls into unintentional works of art.',
      category: 'documentary'
    },
    {
      id: 'set-the-world-on-fire',
      title: 'Set The World on Fire',
      medium: 'Mixed Media',
      year: '2013',
      dimensions: 'Solo Exhibition',
      image: 'https://static.readdy.ai/image/3fdc746f0d1d84f2cde4df046cd392e8/e53a50731e821ee2376e3b98914f5110.jpeg',
      description: 'Set The World on Fire examines the quality of light created by consumer pyrotechnics. Each photograph captures the tension between spectacle and impermanence — moments where fire, color, and smoke transform darkness into something fleetingly sublime. Using fireworks as both subject and light source, the series explores how artificial illumination shapes emotion, perception, and memory. What begins as a simple burst of celebration becomes a study in contrast — beauty and danger, creation and decay — revealing the powerful, unpredictable nature of light itself.',
      category: 'experimental'
    },
    {
      id: 'the-physical-photograph',
      title: 'The Physical Photograph',
      medium: 'Interactive Installation',
      year: '2014',
      dimensions: 'Variable',
      image: 'https://static.readdy.ai/image/3fdc746f0d1d84f2cde4df046cd392e8/5907100388f796c6224647f36e0680a8.jpeg',
      description: 'In an age defined by digital immediacy, the physical photograph is becoming rare — replaced by images that exist everywhere yet are experienced superficially. Once tangible records of people, places, and time, photographs engaged multiple senses and carried the marks of handling and age. Today, endless digital galleries have replaced curated albums, and the act of sharing has lost its intimacy. My photo POD installation reclaims that physical connection, transforming photography into an interactive and sensory experience. By inviting participants to create and share printed images, the work restores the photograph\'s material presence — something to see, touch, and keep, a lasting document of human interaction.',
      category: 'installation'
    },
    {
      id: 'human-connections',
      title: 'Star is Born',
      medium: 'Portrait Photography',
      year: '2019',
      dimensions: '6" x 4"',
      image: 'https://static.readdy.ai/image/3fdc746f0d1d84f2cde4df046cd392e8/f575591aa0d38476e97fbeb3454b41dc.jpeg',
      description: 'Awarded First prize in the Pop Culture Category and "Best use of Theme" overall in the 2019 Los Angeles County Fair\'s Professional Photography Division.',
      category: 'portrait'
    },
    {
      id: 'christmas-card-2021',
      title: 'Christmas Card 2021',
      medium: 'Family Photography',
      year: '2021',
      dimensions: 'Holiday Card',
      image: 'https://static.readdy.ai/image/3fdc746f0d1d84f2cde4df046cd392e8/d762db4615cf943c9c29aed99bf70605.jpeg',
      description: 'Every year we make a Christmas card postcard to send out to friends and family. It\'s a fun family tradition that lets us be festive and creative. This 2021 holiday photograph captures our playful spirit and the joy we find in celebrating the season together.',
      category: 'portrait'
    },
    {
      id: 'kats-spring-training',
      title: 'Kat\'s Spring Training',
      medium: 'Family Photography',
      year: '2022',
      dimensions: '5100px × 3300px',
      image: 'https://static.readdy.ai/image/3fdc746f0d1d84f2cde4df046cd392e8/24ca7227470e213062649801e4f46aa0.jpeg',
      description: 'I\'m constantly inspired by my family. They inspire me to enjoy life and to strive be a better person, better father and better husband. They are, by far, my favorite subject. This image is a composition that was made for a birthday invitation, it placed 2nd in the "People" Category at the 2022 Los Angeles County Fair.',
      category: 'portrait'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Works' },
    { id: 'experimental', name: 'Experimental' },
    { id: 'documentary', name: 'Documentary' },
    { id: 'portrait', name: 'Portrait' },
    { id: 'architectural', name: 'Architectural' },
    { id: 'installation', name: 'Installation' }
  ];

  const filteredArtworks = selectedCategory === 'all' 
    ? artworks 
    : artworks.filter(artwork => artwork.category === selectedCategory);

  const openCarousel = (index: number) => {
    setSelectedArtworkIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeCarousel = () => {
    setSelectedArtworkIndex(null);
    document.body.style.overflow = 'unset';
  };

  const goToPrevious = () => {
    if (selectedArtworkIndex !== null) {
      const newIndex = selectedArtworkIndex === 0 ? filteredArtworks.length - 1 : selectedArtworkIndex - 1;
      setSelectedArtworkIndex(newIndex);
    }
  };

  const goToNext = () => {
    if (selectedArtworkIndex !== null) {
      const newIndex = selectedArtworkIndex === filteredArtworks.length - 1 ? 0 : selectedArtworkIndex + 1;
      setSelectedArtworkIndex(newIndex);
    }
  };

  const currentArtwork = selectedArtworkIndex !== null ? filteredArtworks[selectedArtworkIndex] : null;

  return (
    <section id="gallery" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Selected Works
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            A curated collection of fine art photography exploring themes of light, 
            perception, and human experience
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-6 py-2 rounded-full font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                selectedCategory === category.id
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Artwork Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredArtworks.map((artwork, index) => (
            <article 
              key={artwork.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer"
              onClick={() => openCarousel(index)}
            >
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={artwork.image}
                  alt={artwork.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium">
                    {artwork.medium}
                  </span>
                </div>
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <i className="ri-fullscreen-line text-white text-lg"></i>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="mb-4">
                  <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors duration-300">
                    {artwork.title}
                  </h4>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                    <span>{artwork.year}</span>
                    <span>{artwork.dimensions}</span>
                  </div>
                </div>
                
                <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                  {artwork.description}
                </p>
              </div>
            </article>
          ))}
        </div>

        {/* Contact CTA */}
        {/* Removed CTA section */}
      </div>

      {/* Carousel Modal */}
      {selectedArtworkIndex !== null && currentArtwork && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
          {/* Close Button */}
          <button
            onClick={closeCarousel}
            className="absolute top-6 right-6 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors duration-300 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          {/* Navigation Buttons */}
          <button
            onClick={goToPrevious}
            className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors duration-300 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-2xl"></i>
          </button>

          <button
            onClick={goToNext}
            className="absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors duration-300 cursor-pointer"
          >
            <i className="ri-arrow-right-line text-2xl"></i>
          </button>

          {/* Main Content */}
          <div className="w-full h-full flex items-center justify-center p-6">
            <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Image */}
              <div className="relative">
                <img
                  src={currentArtwork.image}
                  alt={currentArtwork.title}
                  className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
                />
              </div>

              {/* Content */}
              <div className="text-white space-y-6">
                <div>
                  <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-4">
                    {currentArtwork.category.charAt(0).toUpperCase() + currentArtwork.category.slice(1)}
                  </span>
                  <h3 className="text-3xl lg:text-4xl font-bold mb-2">
                    {currentArtwork.title}
                  </h3>
                  <div className="flex items-center gap-4 text-gray-300 mb-6">
                    <span className="font-medium">{currentArtwork.medium}</span>
                    <span>•</span>
                    <span>{currentArtwork.year}</span>
                    <span>•</span>
                    <span>{currentArtwork.dimensions}</span>
                  </div>
                </div>
                
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-gray-200 leading-relaxed">
                    {currentArtwork.description}
                  </p>
                </div>

                {/* Artwork Counter */}
                <div className="flex items-center justify-between pt-6 border-t border-white/20">
                  <span className="text-gray-300">
                    {selectedArtworkIndex + 1} of {filteredArtworks.length}
                  </span>
                  
                  {/* Thumbnail Navigation */}
                  <div className="flex gap-2">
                    {filteredArtworks.slice(Math.max(0, selectedArtworkIndex - 2), selectedArtworkIndex + 3).map((artwork, index) => {
                      const actualIndex = Math.max(0, selectedArtworkIndex - 2) + index;
                      return (
                        <button
                          key={artwork.id}
                          onClick={() => setSelectedArtworkIndex(actualIndex)}
                          className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                            actualIndex === selectedArtworkIndex 
                              ? 'border-white' 
                              : 'border-white/30 hover:border-white/60'
                          }`}
                        >
                          <img
                            src={artwork.image}
                            alt={artwork.title}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Keyboard Navigation Hint */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            Use arrow keys to navigate • ESC to close
          </div>
        </div>
      )}

      {/* Keyboard Navigation */}
      {selectedArtworkIndex !== null && (
        <div
          className="fixed inset-0 pointer-events-none"
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') goToPrevious();
            if (e.key === 'ArrowRight') goToNext();
            if (e.key === 'Escape') closeCarousel();
          }}
          tabIndex={-1}
        />
      )}
    </section>
  );
};

export default ArtworkGallery;
